import { useEffect, useMemo, useRef, useState } from 'react';

import { PAST_DAYS } from '@/constants/general';
import useAuth from '@/hooks/useAuth';
import { Role } from '@/routes/PrivateRoute';
import { centsToCurrency, currencyToCents } from '@/util/number';

import { BillingAccountV2, SkillListItem, SurgeRateV2 } from '../../api/types';
import { JobDraftV2, ShiftDraftV2 } from '../../context';
import { slugifySkillName } from '../JobTypeStep/categoryMeta';

import {
  CalendarCard,
  DateCell,
  DateGrid,
  DateNumber,
  DayHeader,
  DayHeadersRow,
  EmptyCell,
  HeadingBlock,
  HeadingSubtitle,
  HeadingTitle,
  LeftCol,
  MonthLabel,
  MonthNavBtn,
  MonthNavRow,
  PageContent,
  PanelActionsCell,
  PanelColHeaders,
  PanelColLabel,
  PanelDateCell,
  PanelEmpty,
  PanelEmptyText,
  PanelEstAmount,
  PanelEstBlock,
  PanelEstLabel,
  PanelHeader,
  PanelHeaderIcon,
  PanelHeaderTitle,
  PanelHeaderTitleGroup,
  PanelIconBtn,
  PanelRateLabel,
  PanelRateRow,
  PanelRateSection,
  PanelRateValue,
  PanelRow,
  PanelTimeCell,
  PanelTimeMeta,
  PanelTotalsLabel,
  PanelTotalsRow,
  PanelTotalsSection,
  PanelTotalsValue,
  PanelWorkersCell,
  PendingDot,
  Popup,
  PopupAddBtn,
  PopupBody,
  PopupBreakRow,
  PopupCloseBtn,
  PopupDateValue,
  PopupDropdown,
  PopupDropdownItem,
  PopupDropdownScroll,
  PopupFields,
  PopupHeader,
  PopupHeaderTitle,
  PopupHelpText,
  PopupOverlay,
  PopupQtyInput,
  PopupRow,
  PopupRowLabel,
  PopupSelect,
  PopupSelectValue,
  PopupSelectWrap,
  RateCard,
  RateCardSubtitle,
  RateCardTitle,
  RateInput,
  RateInputDollar,
  RateInputGroup,
  RateInputLabel,
  RateInputShell,
  RateInputsRow,
  SchedulePanel,
  TimeChip,
  TzLabel,
} from './styles';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const BREAK_OPTIONS_SHORT = ['0 min', '15 mins', '30 mins', '45 mins'];
const BREAK_OPTIONS_FULL = [
  '0 min',
  '15 mins',
  '30 mins',
  '45 mins',
  '60 mins',
];
const MIN_JOB_MINUTES = 4 * 60;
const BREAK_DISABLED_BELOW_MINUTES = 6 * 60;
const SIXTY_MIN_BREAK_AT_MINUTES = 8 * 60;
const TZ_OFFSET = -4; // EDT (matches prototype)
const TZ_LABEL = 'EDT';

const pad2 = (n: number) => String(n).padStart(2, '0');
const dateKey = (y: number, m: number, d: number) =>
  `${y}-${pad2(m + 1)}-${pad2(d)}`;
const parseKey = (k: string) => {
  const [y, m, d] = k.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const TIME_OPTIONS_15 = (() => {
  const out: string[] = [];
  for (let i = 0; i < 96; i++) {
    const total = i * 15;
    const h24 = Math.floor(total / 60);
    const mn = total % 60;
    const ap = h24 < 12 ? 'AM' : 'PM';
    const h12 = h24 % 12 || 12;
    out.push(`${h12}:${pad2(mn)} ${ap}`);
  }
  return out;
})();

const time12To24 = (t: string) => {
  const m = t.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
  if (!m) return '00:00:00';
  let h = parseInt(m[1], 10);
  const mn = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return `${pad2(h)}:${pad2(mn)}:00`;
};

const time24To12 = (s: string) => {
  const m = s.match(/T?(\d+):(\d+)/);
  if (!m) return '6:00 AM';
  const h = parseInt(m[1], 10);
  const mn = parseInt(m[2], 10);
  const ap = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 || 12;
  return `${h12}:${pad2(mn)} ${ap}`;
};

const breakLabel = (mins: number) => (mins === 0 ? '0 min' : `${mins} mins`);
const breakValue = (label: string) => parseInt(label, 10) || 0;

const labelToMinutes = (t: string) => {
  const m = t.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
  if (!m) return 0;
  let h = parseInt(m[1], 10);
  const mn = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return h * 60 + mn;
};

const durationMinutes = (startLabel: string, endLabel: string) => {
  let diff = labelToMinutes(endLabel) - labelToMinutes(startLabel);
  if (diff <= 0) diff += 24 * 60;
  return diff;
};

const calcDuration = (startLabel: string, endLabel: string, breakMins = 0) => {
  const diff = durationMinutes(startLabel, endLabel);
  const net = Math.max(0, diff - breakMins);
  const hours = net / 60;
  const trimmed = parseFloat(hours.toFixed(2));
  return `${trimmed} hrs`;
};

// ── SVG icons ─────────────────────────────────────────────────────────────

const ChevronLeft = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    style={{ width: 24, height: 24 }}
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRight = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    style={{ width: 24, height: 24 }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const ChevronDown = ({ size = 24 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    style={{ width: size, height: size, flexShrink: 0 }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const ClockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    style={{ width: 24, height: 24, flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const XIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" style={{ width: 20, height: 20 }}>
    <path
      d="M15 5L5 15M5 5l10 10"
      stroke="#1C1C1C"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
const PencilIcon = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="#5B5B5B"
    strokeWidth="1.5"
    style={{ width: 13, height: 13 }}
  >
    <path
      d="M11.5 2.5a1.5 1.5 0 012.12 2.12L5 13.25l-3 .75.75-3L11.5 2.5z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const TrashIcon = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="#CF040C"
    strokeWidth="1.5"
    style={{ width: 13, height: 13 }}
  >
    <path
      d="M6 2h4M3 4h10l-.8 9H3.8L3 4zM6 7v4M10 7v4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Types ─────────────────────────────────────────────────────────────────

type ScheduleStepProps = {
  jobs: JobDraftV2[];
  onUpdateJobs: (jobs: JobDraftV2[]) => void;
  skills: SkillListItem[];
  orderType: 'GIG' | 'LTA' | null;
  billingAccount: BillingAccountV2 | null;
  surgeRates?: SurgeRateV2[];
};

type Entry = {
  dateKey: string;
  startTime: string;
  endTime: string;
  breakTime: string;
  workers: number;
};

type EditTarget = { dateKey: string; entryIdx: number } | null;

// ── Utilities for shifts <-> entries ──────────────────────────────────────

const shiftToEntry = (shift: ShiftDraftV2): Entry => {
  const dk = shift.start_at.slice(0, 10);
  return {
    dateKey: dk,
    startTime: time24To12(shift.start_at),
    endTime: time24To12(shift.end_at),
    breakTime: breakLabel(shift.break_minutes ?? 15),
    workers: shift.quantity ?? 1,
  };
};

const entryToShift = (entry: Entry): ShiftDraftV2 => ({
  start_at: `${entry.dateKey}T${time12To24(entry.startTime)}`,
  end_at: `${entry.dateKey}T${time12To24(entry.endTime)}`,
  time_zone_offset: TZ_OFFSET,
  break_minutes: breakValue(entry.breakTime),
  quantity: entry.workers,
});

const groupedEntries = (shifts: ShiftDraftV2[]) => {
  const groups: Record<string, Entry[]> = {};
  shifts.forEach((s) => {
    const e = shiftToEntry(s);
    (groups[e.dateKey] ||= []).push(e);
  });
  return groups;
};

// ── Component ─────────────────────────────────────────────────────────────

const ScheduleStep = ({
  jobs,
  onUpdateJobs,
  skills,
  orderType,
  billingAccount,
  surgeRates = [],
}: ScheduleStepProps) => {
  // Map of dateKey ("YYYY-MM-DD") → surge rate, used to badge holiday cells
  // and surface a HolidayInfo banner. Mirrors the legacy CreateGigOrder
  // behaviour where surge dates auto-apply a multiplied pay/cost rate.
  const surgeByDate = useMemo(() => {
    const out: Record<string, (typeof surgeRates)[number]> = {};
    for (const sr of surgeRates) out[sr.date] = sr;
    return out;
  }, [surgeRates]);
  const job = jobs[0];
  const shifts = useMemo(() => job?.shifts || [], [job?.shifts]);
  const groups = useMemo(() => groupedEntries(shifts), [shifts]);

  // Pricing visibility mirrors the legacy CreateGigOrder logic: only shown
  // for non-markup (STATIC) billing accounts. Markup accounts hide the rate
  // controls entirely since cost is derived from pay + markup elsewhere.
  const { currentAdmin, currentAdminIsCustomerAdmin: isClientAdmin } =
    useAuth();
  const isTenantAdmin = currentAdmin?.role === Role.TENANT_ADMIN;
  const showPricing = billingAccount?.rate_type === 'STATIC';

  const payRate = job?.pay_rate ?? 0;
  const costRate = job?.cost_rate ?? 0;

  // Local string state lets the user clear / retype the inputs without
  // fighting the cents normalization on every keystroke. We commit on blur.
  const [payInput, setPayInput] = useState<string>(() =>
    payRate ? centsToCurrency(payRate) : '',
  );
  const [costInput, setCostInput] = useState<string>(() =>
    costRate ? centsToCurrency(costRate) : '',
  );

  // Re-sync local string inputs when the underlying job rates change from
  // outside the inputs (e.g. the rate-quote prefetch lands).
  useEffect(() => {
    setPayInput(payRate ? centsToCurrency(payRate) : '');
  }, [payRate]);
  useEffect(() => {
    setCostInput(costRate ? centsToCurrency(costRate) : '');
  }, [costRate]);

  const commitRate = (field: 'pay_rate' | 'cost_rate', raw: string) => {
    const trimmed = raw.trim();
    const cents = trimmed ? Math.round(currencyToCents(trimmed)) : 0;
    if (Number.isNaN(cents)) return;
    const nextJobs = [...jobs];
    nextJobs[0] = { ...nextJobs[0], [field]: cents };
    onUpdateJobs(nextJobs);
  };

  const allEntries = useMemo(() => {
    const flat: { dateKey: string; entryIdx: number; entry: Entry }[] = [];
    Object.keys(groups)
      .sort()
      .forEach((dk) => {
        groups[dk].forEach((entry, idx) => {
          flat.push({ dateKey: dk, entryIdx: idx, entry });
        });
      });
    return flat;
  }, [groups]);

  const selectedSkill = useMemo(
    () => skills.find((s) => s.id === job?.skill_id) ?? null,
    [skills, job?.skill_id],
  );

  // Calendar view month/year
  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  // Popup state
  const [popupKey, setPopupKey] = useState<string | null>(null);
  const [popupAnchor, setPopupAnchor] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [editTarget, setEditTarget] = useState<EditTarget>(null);

  // Pending date (clicked but not yet saved)
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  // Popup form state
  const [startTime, setStartTime] = useState('6:00 AM');
  const [endTime, setEndTime] = useState('3:00 PM');
  const [breakTime, setBreakTime] = useState('15 mins');
  const [workers, setWorkers] = useState(1);
  const [openDropdown, setOpenDropdown] = useState<
    'start' | 'end' | 'break' | null
  >(null);

  // Last-set memory for pre-fill on the next fresh date
  const lastTimes = useRef<{
    start: string;
    end: string;
    brk: string;
    workers: number;
  } | null>(null);

  // Shift duration & break constraints derived from popup form
  const popupDurationMins = useMemo(
    () => durationMinutes(startTime, endTime),
    [startTime, endTime],
  );
  const breakDisabled = popupDurationMins < BREAK_DISABLED_BELOW_MINUTES;
  const breakOptions =
    popupDurationMins >= SIXTY_MIN_BREAK_AT_MINUTES
      ? BREAK_OPTIONS_FULL
      : BREAK_OPTIONS_SHORT;
  const durationTooShort = popupDurationMins < MIN_JOB_MINUTES;

  useEffect(() => {
    if (!popupKey) return;
    if (breakDisabled && breakTime !== '0 min') {
      setBreakTime('0 min');
    } else if (!breakDisabled && !breakOptions.includes(breakTime)) {
      setBreakTime(breakOptions[breakOptions.length - 1]);
    }
  }, [popupKey, breakDisabled, breakOptions, breakTime]);

  // ── Calendar grid ──────────────────────────────────────────────────────

  const orderTypeLabel = (orderType || 'GIG').toLowerCase();
  const headingTitle = `Choose ${orderType === 'LTA' ? 'LTA' : 'Gig'} schedule`;
  const monthLabel = `${MONTH_NAMES[viewMonth]} ${viewYear}`;
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const todayKey = dateKey(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  // Tenant admins may backdate shifts within the last PAST_DAYS window
  // (matches legacy CreateGigOrder behavior). Customer admins cannot pick
  // any past dates.
  const minSelectableKey = useMemo(() => {
    if (!isTenantAdmin) return todayKey;
    const floor = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - PAST_DAYS,
    );
    return dateKey(floor.getFullYear(), floor.getMonth(), floor.getDate());
  }, [today, todayKey, isTenantAdmin]);

  const goPrev = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };
  const goNext = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  // ── Popup positioning ──────────────────────────────────────────────────

  const positionPopupNearRect = (rect: DOMRect) => {
    const pw = 400;
    const ph = 380;
    const gap = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left =
      rect.right + gap + pw <= vw ? rect.right + gap : rect.left - gap - pw;
    if (left + pw > vw - 12) left = vw - pw - 12;
    if (left < 12) left = 12;
    let top = rect.top;
    if (top + ph > vh - 12) top = vh - ph - 12;
    if (top < 12) top = 12;
    return { left: Math.round(left), top: Math.round(top) };
  };

  const positionPopupCentered = () => {
    const pw = 400;
    const ph = 380;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return {
      left: Math.round((vw - pw) / 2),
      top: Math.round(Math.max(40, (vh - ph) / 2)),
    };
  };

  const openPopupForDate = (
    key: string,
    anchor: 'rect' | 'center',
    rect?: DOMRect,
    target: EditTarget = null,
  ) => {
    setEditTarget(target);
    setOpenDropdown(null);
    if (target) {
      const existing = groups[target.dateKey]?.[target.entryIdx];
      if (existing) {
        setStartTime(existing.startTime);
        setEndTime(existing.endTime);
        setBreakTime(existing.breakTime);
        setWorkers(existing.workers);
      }
    } else {
      const last = lastTimes.current;
      setStartTime(last?.start || '6:00 AM');
      setEndTime(last?.end || '3:00 PM');
      setBreakTime(last?.brk || '15 mins');
      setWorkers(last?.workers || 1);
    }
    setPopupKey(key);
    setPopupAnchor(
      anchor === 'rect' && rect
        ? positionPopupNearRect(rect)
        : positionPopupCentered(),
    );
  };

  const closePopup = () => {
    if (pendingKey === popupKey) setPendingKey(null);
    setPopupKey(null);
    setPopupAnchor(null);
    setEditTarget(null);
    setOpenDropdown(null);
  };

  const handleCellClick = (
    key: string,
    ev: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (key < minSelectableKey) return;
    const rect = (ev.currentTarget as HTMLDivElement).getBoundingClientRect();
    const hasEntries = (groups[key]?.length || 0) > 0;
    if (popupKey === key && !editTarget) {
      closePopup();
      return;
    }
    if (!hasEntries) setPendingKey(key);
    else setPendingKey(null);
    openPopupForDate(key, 'rect', rect, null);
  };

  // ── Shift mutators ────────────────────────────────────────────────────

  const writeShifts = (
    next: ShiftDraftV2[],
    lastWorkers?: number,
    lastBreak?: number,
  ) => {
    const nextJobs = [...jobs];
    nextJobs[0] = {
      ...nextJobs[0],
      shifts: next,
      quantity: lastWorkers ?? nextJobs[0]?.quantity ?? 1,
      mandatory_break_time:
        lastBreak ?? nextJobs[0]?.mandatory_break_time ?? 30,
    };
    onUpdateJobs(nextJobs);
  };

  const handleAdd = () => {
    if (!popupKey || durationTooShort) return;
    const entry: Entry = {
      dateKey: popupKey,
      startTime,
      endTime,
      breakTime,
      workers,
    };
    lastTimes.current = {
      start: startTime,
      end: endTime,
      brk: breakTime,
      workers,
    };

    let nextShifts: ShiftDraftV2[];
    if (editTarget) {
      const flat = [...shifts];
      let counter = 0;
      const targetAbs = flat.findIndex((s) => {
        if (s.start_at.slice(0, 10) !== editTarget.dateKey) return false;
        const isMatch = counter === editTarget.entryIdx;
        counter++;
        return isMatch;
      });
      if (targetAbs !== -1) flat[targetAbs] = entryToShift(entry);
      nextShifts = flat;
    } else {
      nextShifts = [...shifts, entryToShift(entry)];
    }
    writeShifts(nextShifts, workers, breakValue(breakTime));
    setPendingKey(null);
    closePopup();
  };

  const handleRemoveEntry = (dk: string, idx: number) => {
    let counter = 0;
    const next = shifts.filter((s) => {
      if (s.start_at.slice(0, 10) !== dk) return true;
      const keep = counter !== idx;
      counter++;
      return keep;
    });
    writeShifts(next);
  };

  const handleEditEntry = (dk: string, ei: number) => {
    openPopupForDate(dk, 'center', undefined, { dateKey: dk, entryIdx: ei });
  };

  // ── Calendar cells ────────────────────────────────────────────────────

  const renderCells = () => {
    const cells: React.ReactNode[] = [];
    let d = 1;
    for (let row = 0; row < 6; row++) {
      for (let col = 0; col < 7; col++) {
        const idx = row * 7 + col;
        if (idx < firstDayOfMonth || d > daysInMonth) {
          cells.push(<EmptyCell key={`empty-${idx}`} />);
        } else {
          const k = dateKey(viewYear, viewMonth, d);
          const entries = groups[k] || [];
          const hasEntries = entries.length > 0;
          const isToday = k === todayKey;
          const isPopupOpenHere = popupKey === k && !editTarget;
          const isPending = pendingKey === k && !hasEntries;
          const isDisabled = k < minSelectableKey;
          const highlighted =
            !isDisabled && (hasEntries || isPopupOpenHere || isPending);
          const cellState: 'default' | 'today' | 'disabled' = isDisabled
            ? 'disabled'
            : isToday && !hasEntries
              ? 'today'
              : 'default';
          const dayNum = d;
          const surge = surgeByDate[k];
          cells.push(
            <DateCell
              key={k}
              highlighted={highlighted}
              disabled={isDisabled}
              onClick={(ev) => handleCellClick(k, ev)}
              title={
                surge
                  ? `Holiday rate: ${surge.rate}× ${surge.description ? `(${surge.description})` : ''}`.trim()
                  : undefined
              }
            >
              <DateNumber state={cellState}>{dayNum}</DateNumber>
              {surge ? (
                <span
                  aria-label="Holiday rate"
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    background: '#FFEAD2',
                    color: '#A8540B',
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '0 4px',
                    borderRadius: 4,
                    lineHeight: '14px',
                  }}
                >
                  {surge.rate}×
                </span>
              ) : null}
              {entries.map((e, i) => (
                <TimeChip key={i}>{e.startTime}</TimeChip>
              ))}
              {!hasEntries && (isPopupOpenHere || isPending) ? (
                <PendingDot />
              ) : null}
            </DateCell>,
          );
          d++;
        }
      }
      if (d > daysInMonth) break;
    }
    return cells;
  };

  // ── Right side panel ──────────────────────────────────────────────────

  const skillDisplayName =
    selectedSkill?.display_name_en || selectedSkill?.name || 'Schedule';
  const skillIconUrl = selectedSkill?.image_url || null;

  // ── Render ────────────────────────────────────────────────────────────

  // ── Totals for the right-side summary panel ──────────────────────────
  // Hours and dollar totals roll up across all entries. Each entry's hours
  // already exclude the break minutes (matching the legacy estimate basis).
  const totalShiftMinutes = useMemo(() => {
    return allEntries.reduce((acc, { entry }) => {
      const dur = durationMinutes(entry.startTime, entry.endTime);
      const brk = breakValue(entry.breakTime);
      const net = Math.max(0, dur - brk);
      return acc + net * entry.workers;
    }, 0);
  }, [allEntries]);
  const totalHoursDecimal = totalShiftMinutes / 60;
  const totalHoursTrimmed = parseFloat(totalHoursDecimal.toFixed(2));
  const totalHoursLabel = `${totalHoursTrimmed} hrs`;

  // Surge-aware totals: shifts on a surge date are multiplied by `rate`.
  // Mirrors the legacy SummaryTableRevamped/PayRateStep computation so the
  // panel pay/estimate shown here matches what the backend will bill.
  const { totalEstimateCents, totalPayCents, holidayEntries } = useMemo(() => {
    let estimate = 0;
    let pay = 0;
    const holidays: {
      dateKey: string;
      rate: number;
      description?: string | null;
    }[] = [];
    const seenDates = new Set<string>();
    for (const { dateKey: dk, entry } of allEntries) {
      const dur = durationMinutes(entry.startTime, entry.endTime);
      const brk = breakValue(entry.breakTime);
      const net = Math.max(0, dur - brk);
      const minutes = net * entry.workers;
      const surge = surgeByDate[dk];
      const multiplier = surge?.rate ?? 1;
      estimate += Math.round((costRate * multiplier * minutes) / 60);
      pay += Math.round((payRate * multiplier * minutes) / 60);
      if (surge && !seenDates.has(dk)) {
        seenDates.add(dk);
        holidays.push({
          dateKey: dk,
          rate: surge.rate,
          description: surge.description,
        });
      }
    }
    return {
      totalEstimateCents: estimate,
      totalPayCents: pay,
      holidayEntries: holidays,
    };
  }, [allEntries, costRate, payRate, surgeByDate]);

  return (
    <PageContent>
      <LeftCol>
        <HeadingBlock>
          <HeadingTitle>{headingTitle}</HeadingTitle>
          <HeadingSubtitle>
            Pick one or more dates to set shifts
          </HeadingSubtitle>
        </HeadingBlock>

        {showPricing ? (
          <RateCard>
            <RateCardTitle>Job rate</RateCardTitle>
            <RateCardSubtitle>
              {isClientAdmin
                ? 'Bill rate for this job.'
                : 'Set the pay and bill rates for this job.'}
            </RateCardSubtitle>
            <RateInputsRow>
              {!isClientAdmin ? (
                <RateInputGroup>
                  <RateInputLabel htmlFor="job-pay-rate">
                    Pay rate
                  </RateInputLabel>
                  <RateInputShell>
                    <RateInputDollar>$</RateInputDollar>
                    <RateInput
                      id="job-pay-rate"
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={payInput}
                      onChange={(e) => setPayInput(e.target.value)}
                      onBlur={() => commitRate('pay_rate', payInput)}
                    />
                  </RateInputShell>
                </RateInputGroup>
              ) : null}
              <RateInputGroup>
                <RateInputLabel htmlFor="job-bill-rate">
                  Bill rate
                </RateInputLabel>
                <RateInputShell readOnly={isClientAdmin}>
                  <RateInputDollar>$</RateInputDollar>
                  <RateInput
                    id="job-bill-rate"
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={costInput}
                    readOnly={isClientAdmin}
                    onChange={(e) => setCostInput(e.target.value)}
                    onBlur={() => commitRate('cost_rate', costInput)}
                  />
                </RateInputShell>
              </RateInputGroup>
            </RateInputsRow>
          </RateCard>
        ) : null}

        {holidayEntries.length > 0 ? (
          <div
            role="status"
            style={{
              border: '1px solid #FFD49A',
              background: '#FFF6E8',
              color: '#7A4309',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 13,
              lineHeight: 1.4,
            }}
          >
            <strong>Holiday surge pricing applies</strong> on{' '}
            {holidayEntries
              .map((h) => {
                const dt = parseKey(h.dateKey);
                return `${MONTH_ABBR[dt.getMonth()]} ${dt.getDate()} (${h.rate}×)`;
              })
              .join(', ')}
            . Pay and bill rates on these dates are multiplied automatically and
            submitted as separate jobs with{' '}
            <code style={{ fontSize: 12 }}>is_holiday: true</code>.
          </div>
        ) : null}

        <CalendarCard>
          <MonthNavRow>
            <MonthNavBtn
              type="button"
              onClick={goPrev}
              aria-label="Previous month"
            >
              <ChevronLeft />
            </MonthNavBtn>
            <MonthLabel>{monthLabel}</MonthLabel>
            <MonthNavBtn type="button" onClick={goNext} aria-label="Next month">
              <ChevronRight />
            </MonthNavBtn>
          </MonthNavRow>
          <DayHeadersRow>
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, i) => (
              <DayHeader key={i}>{label}</DayHeader>
            ))}
          </DayHeadersRow>
          <DateGrid>{renderCells()}</DateGrid>
        </CalendarCard>
      </LeftCol>

      <SchedulePanel>
        <PanelHeader tone={showPricing ? 'pricing' : 'default'}>
          <PanelHeaderTitleGroup>
            {skillIconUrl ? (
              <PanelHeaderIcon
                src={skillIconUrl}
                alt={skillDisplayName}
                data-slug={
                  selectedSkill
                    ? slugifySkillName(selectedSkill.name)
                    : undefined
                }
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : null}
            <PanelHeaderTitle>{skillDisplayName}</PanelHeaderTitle>
          </PanelHeaderTitleGroup>
          {showPricing && !isClientAdmin ? (
            <PanelEstBlock>
              <PanelEstAmount>${centsToCurrency(totalPayCents)}</PanelEstAmount>
              <PanelEstLabel>Est. Pay</PanelEstLabel>
            </PanelEstBlock>
          ) : null}
        </PanelHeader>

        {allEntries.length === 0 ? (
          <PanelEmpty>
            <PanelEmptyText>
              No shifts yet — pick dates on the calendar.
            </PanelEmptyText>
          </PanelEmpty>
        ) : (
          <>
            <PanelColHeaders>
              <PanelColLabel>Date</PanelColLabel>
              <PanelColLabel>Time</PanelColLabel>
              <PanelColLabel>Workers</PanelColLabel>
              <PanelColLabel />
            </PanelColHeaders>
            {allEntries.map(({ dateKey: dk, entryIdx, entry }, i) => {
              const dt = parseKey(dk);
              const dateLabel = `${DAY_ABBR[dt.getDay()]}, ${MONTH_ABBR[dt.getMonth()]} ${dt.getDate()}`;
              const dur = calcDuration(
                entry.startTime,
                entry.endTime,
                breakValue(entry.breakTime),
              );
              const brk =
                entry.breakTime && entry.breakTime !== '0 min'
                  ? `${entry.breakTime} break · `
                  : '';
              const isLast = i === allEntries.length - 1;
              return (
                <PanelRow key={`${dk}-${entryIdx}`} last={isLast}>
                  <PanelDateCell>{dateLabel}</PanelDateCell>
                  <PanelTimeCell>
                    {entry.startTime} – {entry.endTime}
                    <br />
                    <PanelTimeMeta>
                      {brk}
                      {dur}
                    </PanelTimeMeta>
                  </PanelTimeCell>
                  <PanelWorkersCell>{entry.workers}</PanelWorkersCell>
                  <PanelActionsCell>
                    <PanelIconBtn
                      type="button"
                      aria-label="Edit shift"
                      onClick={() => handleEditEntry(dk, entryIdx)}
                    >
                      <PencilIcon />
                    </PanelIconBtn>
                    <PanelIconBtn
                      type="button"
                      aria-label="Remove shift"
                      onClick={() => handleRemoveEntry(dk, entryIdx)}
                    >
                      <TrashIcon />
                    </PanelIconBtn>
                  </PanelActionsCell>
                </PanelRow>
              );
            })}
          </>
        )}

        {showPricing ? (
          <>
            <PanelRateSection>
              {!isClientAdmin ? (
                <PanelRateRow>
                  <PanelRateLabel>Pay rate</PanelRateLabel>
                  <PanelRateValue>${centsToCurrency(payRate)}</PanelRateValue>
                </PanelRateRow>
              ) : null}
              <PanelRateRow>
                <PanelRateLabel>Bill rate</PanelRateLabel>
                <PanelRateValue>${centsToCurrency(costRate)}</PanelRateValue>
              </PanelRateRow>
            </PanelRateSection>
            <PanelTotalsSection>
              <PanelTotalsRow>
                <PanelTotalsLabel weight="regular">
                  Total Hours
                </PanelTotalsLabel>
                <PanelTotalsValue>{totalHoursLabel}</PanelTotalsValue>
              </PanelTotalsRow>
              <PanelTotalsRow>
                <PanelTotalsLabel weight="bold">
                  Total Estimate
                </PanelTotalsLabel>
                <PanelTotalsValue>
                  ${centsToCurrency(totalEstimateCents)}
                </PanelTotalsValue>
              </PanelTotalsRow>
            </PanelTotalsSection>
          </>
        ) : null}
      </SchedulePanel>

      {/* Popup */}
      {popupKey && popupAnchor ? (
        <>
          <PopupOverlay dim={!!editTarget} onClick={closePopup} />
          <Popup style={{ left: popupAnchor.left, top: popupAnchor.top }}>
            <PopupHeader>
              <PopupHeaderTitle>
                {editTarget
                  ? 'Edit shift'
                  : `Create ${orderTypeLabel === 'lta' ? 'LTA' : 'gig'} shift`}
              </PopupHeaderTitle>
              <PopupCloseBtn
                type="button"
                onClick={closePopup}
                aria-label="Close popup"
              >
                <XIcon />
              </PopupCloseBtn>
            </PopupHeader>
            <PopupBody>
              <PopupFields>
                <PopupRow>
                  <PopupRowLabel>Date</PopupRowLabel>
                  <PopupDateValue>
                    {(() => {
                      const dt = parseKey(popupKey);
                      return `${DAY_ABBR[dt.getDay()]}, ${MONTH_ABBR[dt.getMonth()]} ${dt.getDate()}`;
                    })()}
                  </PopupDateValue>
                </PopupRow>

                <PopupRow>
                  <PopupRowLabel>Time</PopupRowLabel>
                  <PopupSelectWrap>
                    <PopupSelect
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === 'start' ? null : 'start',
                        )
                      }
                    >
                      <PopupSelectValue placeholder={false}>
                        {startTime}
                      </PopupSelectValue>
                      <ClockIcon />
                    </PopupSelect>
                    {openDropdown === 'start' ? (
                      <PopupDropdown>
                        <PopupDropdownScroll>
                          {TIME_OPTIONS_15.map((t) => (
                            <PopupDropdownItem
                              key={t}
                              onClick={() => {
                                setStartTime(t);
                                const idx = TIME_OPTIONS_15.indexOf(t);
                                if (idx !== -1)
                                  setEndTime(TIME_OPTIONS_15[(idx + 16) % 96]);
                                setOpenDropdown(null);
                              }}
                            >
                              {t}
                            </PopupDropdownItem>
                          ))}
                        </PopupDropdownScroll>
                      </PopupDropdown>
                    ) : null}
                  </PopupSelectWrap>
                  <PopupSelectWrap>
                    <PopupSelect
                      onClick={() =>
                        setOpenDropdown(openDropdown === 'end' ? null : 'end')
                      }
                    >
                      <PopupSelectValue placeholder={false}>
                        {endTime}
                      </PopupSelectValue>
                      <ClockIcon />
                    </PopupSelect>
                    {openDropdown === 'end' ? (
                      <PopupDropdown>
                        <PopupDropdownScroll>
                          {TIME_OPTIONS_15.map((t) => (
                            <PopupDropdownItem
                              key={t}
                              onClick={() => {
                                setEndTime(t);
                                setOpenDropdown(null);
                              }}
                            >
                              {t}
                            </PopupDropdownItem>
                          ))}
                        </PopupDropdownScroll>
                      </PopupDropdown>
                    ) : null}
                  </PopupSelectWrap>
                  <TzLabel>{TZ_LABEL}</TzLabel>
                </PopupRow>

                <PopupRow>
                  <PopupRowLabel>Break</PopupRowLabel>
                  <PopupBreakRow>
                    <PopupSelectWrap>
                      <PopupSelect
                        disabled={breakDisabled}
                        onClick={() => {
                          if (breakDisabled) return;
                          setOpenDropdown(
                            openDropdown === 'break' ? null : 'break',
                          );
                        }}
                      >
                        <PopupSelectValue placeholder={false}>
                          {breakTime}
                        </PopupSelectValue>
                        <ChevronDown />
                      </PopupSelect>
                      {openDropdown === 'break' && !breakDisabled ? (
                        <PopupDropdown>
                          {breakOptions.map((opt) => (
                            <PopupDropdownItem
                              key={opt}
                              onClick={() => {
                                setBreakTime(opt);
                                setOpenDropdown(null);
                              }}
                            >
                              {opt}
                            </PopupDropdownItem>
                          ))}
                        </PopupDropdown>
                      ) : null}
                    </PopupSelectWrap>
                    {breakDisabled ? (
                      <PopupHelpText tone="info">
                        Shifts shorter than 6 hours may not have mandatory break
                        times.
                      </PopupHelpText>
                    ) : null}
                  </PopupBreakRow>
                </PopupRow>

                <PopupRow>
                  <PopupRowLabel>Quantity</PopupRowLabel>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <PopupQtyInput
                      type="number"
                      min={1}
                      value={workers}
                      onChange={(e) =>
                        setWorkers(
                          Math.max(1, parseInt(e.target.value, 10) || 1),
                        )
                      }
                    />
                  </div>
                </PopupRow>
              </PopupFields>
              <PopupAddBtn
                type="button"
                onClick={handleAdd}
                disabled={durationTooShort}
              >
                {editTarget ? 'Save' : 'Add'}
              </PopupAddBtn>
              {durationTooShort ? (
                <PopupHelpText tone="error">
                  Job duration should be at least 4 hours.
                </PopupHelpText>
              ) : null}
            </PopupBody>
          </Popup>
        </>
      ) : null}
    </PageContent>
  );
};

export default ScheduleStep;
